import { httpClient } from '../../api/httpClient';
import {
  DashboardState,
  PaymentCreatePayload,
  PaymentCreateResponse,
  PaymentStatusResponse,
  RemnaDevice,
  RemnaDevicesResponse,
  RemnaSubResponse,
  SubscriptionInfo,
  SubscriptionPlan,
  TrialStatus,
  UserState,
  WalletInfo,
} from './cabinet.schemas';
import { toSafeNumber } from './cabinet.utils';

function normalizeDevicesResponse(data: RemnaDevicesResponse | RemnaDevice[]) {
  if (Array.isArray(data)) {
    return {
      total: data.length,
      devices: data,
    };
  }

  const payload = data.response || data;
  const devices = payload.devices || [];

  return {
    total: toSafeNumber(payload.total, devices.length),
    devices,
  };
}

export async function getDashboardState(): Promise<DashboardState> {
  const [subResult, devicesResult, stateResult, trialResult] = await Promise.allSettled([
    httpClient.get<SubscriptionInfo | null>('/v2/me/sub'),
    httpClient.get<RemnaDevicesResponse | RemnaDevice[]>('/v2/me/remna/devices'),
    httpClient.get<UserState>('/v2/me/state'),
    httpClient.get<TrialStatus>('/v2/me/trial/status'),
  ]);

  const devicesPayload = devicesResult.status === 'fulfilled'
    ? normalizeDevicesResponse(devicesResult.value.data)
    : { total: 0, devices: [] };

  return {
    subInfo: subResult.status === 'fulfilled' ? subResult.value.data : null,
    devices: devicesPayload.devices,
    devicesTotal: devicesPayload.total,
    userState: stateResult.status === 'fulfilled' ? stateResult.value.data : null,
    trialStatus: trialResult.status === 'fulfilled' ? trialResult.value.data : null,
  };
}

export async function getPaidSubscriptions() {
  const response = await httpClient.get<SubscriptionPlan[]>('/v2/subscriptions/active/paid');
  return response.data;
}

export async function getWallet() {
  const response = await httpClient.get<WalletInfo>('/v2/me/wallet');
  return response.data;
}

export async function createCombinedPayment(payload: PaymentCreatePayload) {
  const response = await httpClient.post<PaymentCreateResponse>('/v2/me/payments/create-combined', payload);
  return response.data;
}

export async function getPaymentStatus(ykPaymentId: string) {
  const response = await httpClient.get<PaymentStatusResponse>(`/v2/me/payments/${ykPaymentId}/status`);
  return response.data;
}

export async function getTrialStatus() {
  const response = await httpClient.get<TrialStatus>('/v2/me/trial/status');
  return response.data;
}

export async function activateTrial(): Promise<TrialStatus | UserState | null> {
  const response = await httpClient.post<unknown>('/v2/me/trial/activate');
  const data = response.data;

  if (data && typeof data === 'object' && !Array.isArray(data)) {
    return data as TrialStatus | UserState;
  }

  return null;
}

export async function deleteRemnaDevice(hwid: string) {
  await httpClient.delete(`/v2/me/remna/devices/${encodeURIComponent(hwid)}`);
}

export async function getRemnaSub() {
  const response = await httpClient.get<RemnaSubResponse>('/v2/me/remna/sub');
  return response.data;
}
